import { useEffect, useRef } from 'react'

/**
 * Balatro-style swirl background — ported from reactbits.dev/backgrounds/balatro
 * Uses Canvas 2D at low resolution to avoid WebGL context conflicts with Phaser.
 * CSS bilinear upscale smooths the pixelation into an organic look.
 */

const RENDER_W = 280
const RENDER_H = 158
const TARGET_FPS = 24
const FRAME_MS = 1000 / TARGET_FPS

// Boss color palette
const COLOR1 = [0.87, 0.27, 0.23] as const  // #DE443B deep red
const COLOR2 = [0.0, 0.42, 0.71] as const   // #006BB4 blue
const COLOR3 = [0.086, 0.137, 0.145] as const // #162325 dark

// Effect parameters (tuned for boss vibe)
const SPIN_ROTATION = -2.0
const SPIN_SPEED = 7.0
const CONTRAST = 3.5
const LIGHTING = 0.4
const SPIN_AMOUNT = 0.25
const SPIN_EASE = 1.0
const OFFSET_X = 0.0
const OFFSET_Y = 0.0

export function FluidCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    canvas.width = RENDER_W
    canvas.height = RENDER_H
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const imgData = ctx.createImageData(RENDER_W, RENDER_H)
    const buf = imgData.data
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    // Precompute resolution values
    const resX = RENDER_W
    const resY = RENDER_H
    const resLen = Math.sqrt(resX * resX + resY * resY)
    const midX = (resX / resLen) / 2
    const midY = (resY / resLen) / 2

    function renderFrame(iTime: number) {
      const speed0 = SPIN_ROTATION * SPIN_EASE * 0.2 * iTime + 302.2
      const baseSpeed = iTime * SPIN_SPEED

      for (let py = 0; py < RENDER_H; py++) {
        for (let px = 0; px < RENDER_W; px++) {
          // Smooth UV (no pixelation quantization — we want fluid, not retro)
          let uvX = (px - 0.5 * resX) / resLen - OFFSET_X
          let uvY = (py - 0.5 * resY) / resLen - OFFSET_Y
          const uvLen = Math.sqrt(uvX * uvX + uvY * uvY)

          // Spin distortion
          const newAngle = Math.atan2(uvY, uvX) + speed0 - SPIN_EASE * 20.0 * (SPIN_AMOUNT * uvLen + (1.0 - SPIN_AMOUNT))
          uvX = uvLen * Math.cos(newAngle) + midX - midX
          uvY = uvLen * Math.sin(newAngle) + midY - midY

          uvX *= 30.0
          uvY *= 30.0

          let uv2x = uvX + uvY
          let uv2y = uvX + uvY

          // 5-iteration warp loop (core of the Balatro look)
          for (let i = 0; i < 5; i++) {
            const maxUV = uvX > uvY ? uvX : uvY
            const sinMax = Math.sin(maxUV)
            uv2x += sinMax + uvX
            uv2y += sinMax + uvY
            uvX += 0.5 * Math.cos(5.1123314 + 0.353 * uv2y + baseSpeed * 0.131121)
            uvY += 0.5 * Math.sin(uv2x - 0.113 * baseSpeed)
            const cosSum = Math.cos(uvX + uvY)
            const sinDiff = Math.sin(uvX * 0.711 - uvY)
            uvX -= cosSum - sinDiff
            uvY -= cosSum - sinDiff
          }

          // Color blending
          const contrastMod = 0.25 * CONTRAST + 0.5 * SPIN_AMOUNT + 1.2
          const paintRes = Math.min(2.0, Math.max(0.0, Math.sqrt(uvX * uvX + uvY * uvY) * 0.035 * contrastMod))
          const c1p = Math.max(0.0, 1.0 - contrastMod * Math.abs(1.0 - paintRes))
          const c2p = Math.max(0.0, 1.0 - contrastMod * Math.abs(paintRes))
          const c3p = 1.0 - Math.min(1.0, c1p + c2p)
          const light = (LIGHTING - 0.2) * Math.max(c1p * 5.0 - 4.0, 0.0) + LIGHTING * Math.max(c2p * 5.0 - 4.0, 0.0)

          const base = 0.3 / CONTRAST
          const blend = 1.0 - base

          let cr = base * COLOR1[0] + blend * (COLOR1[0] * c1p + COLOR2[0] * c2p + c3p * COLOR3[0]) + light
          let cg = base * COLOR1[1] + blend * (COLOR1[1] * c1p + COLOR2[1] * c2p + c3p * COLOR3[1]) + light
          let cb = base * COLOR1[2] + blend * (COLOR1[2] * c1p + COLOR2[2] * c2p + c3p * COLOR3[2]) + light

          // Clamp
          if (cr > 1) cr = 1; else if (cr < 0) cr = 0
          if (cg > 1) cg = 1; else if (cg < 0) cg = 0
          if (cb > 1) cb = 1; else if (cb < 0) cb = 0

          const idx = (py * RENDER_W + px) * 4
          buf[idx]     = (cr * 255) | 0
          buf[idx + 1] = (cg * 255) | 0
          buf[idx + 2] = (cb * 255) | 0
          buf[idx + 3] = 255
        }
      }
      ctx!.putImageData(imgData, 0, 0)
    }

    renderFrame(0)
    if (reducedMotion) return

    let rafId = 0
    let lastFrame = 0
    const start = performance.now()

    function animate(now: number) {
      if (now - lastFrame >= FRAME_MS) {
        lastFrame = now
        renderFrame((now - start) / 1000)
      }
      rafId = requestAnimationFrame(animate)
    }
    rafId = requestAnimationFrame(animate)
    return () => { cancelAnimationFrame(rafId) }
  }, [])

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 0, opacity: 0.55 }}
    >
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '100%', display: 'block', imageRendering: 'auto', filter: 'blur(4px)' }}
      />
    </div>
  )
}
